import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useOutletContext } from 'react-router-dom'
import { Search, Copy, X, Plus, Link2, ChevronDown, ChevronUp } from 'lucide-react'
import { useAuth } from '../context/AuthContext'
import {
  getAdminSaleOrders,
  checkInTicket,
  getCheckinSessions,
  createCheckinSession,
  revokeCheckinSession,
  type AdminSaleOrderWithPhase,
  type CheckinSession,
} from '../lib/api'
import type { SaleEventDetailContext } from './SaleEventDetailLayout'
import { useConfirm } from '../components/ConfirmDialog'

function SessionsPanel({ eventId, authToken }: { eventId: string; authToken: string }) {
  const [sessions, setSessions] = useState<CheckinSession[]>([])
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)
  const [newName, setNewName] = useState('')
  const [showForm, setShowForm] = useState(false)
  const [copied, setCopied] = useState<string | null>(null)
  const { confirm, dialog } = useConfirm()

  async function load() {
    setLoading(true)
    try { setSessions(await getCheckinSessions(authToken, eventId)) }
    finally { setLoading(false) }
  }

  useEffect(() => { load() }, [eventId, authToken])

  async function handleCreate() {
    if (!newName.trim()) return
    setCreating(true)
    try {
      const session = await createCheckinSession(authToken, eventId, newName.trim())
      setSessions(prev => [session, ...prev])
      setShowForm(false)
      setNewName('')
      const link = `${window.location.origin}/checkin/${session.token}`
      await navigator.clipboard.writeText(link)
      setCopied(session.id)
      setTimeout(() => setCopied(null), 2500)
    } finally {
      setCreating(false)
    }
  }

  async function handleRevoke(session: CheckinSession) {
    const ok = await confirm(
      `Sesi "${session.operator_name}" akan dinonaktifkan. Operator tidak bisa akses link lagi.`,
      { title: 'Nonaktifkan Sesi' }
    )
    if (!ok) return
    await revokeCheckinSession(authToken, session.id)
    setSessions(prev => prev.map(s => s.id === session.id ? { ...s, is_active: false } : s))
  }

  async function handleCopy(session: CheckinSession) {
    const link = `${window.location.origin}/checkin/${session.token}`
    await navigator.clipboard.writeText(link)
    setCopied(session.id)
    setTimeout(() => setCopied(null), 2500)
  }

  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-900 overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800">
        <div>
          <p className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">Operator Sessions</p>
          <p className="text-xs text-zinc-600 mt-0.5">Link akses check-in untuk petugas tanpa akun admin</p>
        </div>
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg transition-colors"
          >
            <Plus size={12} />
            Buat Sesi
          </button>
        )}
      </div>

      {showForm && (
        <div className="px-5 py-4 border-b border-zinc-800 bg-zinc-800/30 flex items-center gap-3">
          <input
            autoFocus
            type="text"
            value={newName}
            onChange={e => setNewName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleCreate()}
            placeholder="Nama operator, mis. Andi - Gate 1"
            className="flex-1 bg-zinc-800 text-white rounded-lg px-3 py-2 text-sm border border-zinc-700 focus:outline-none focus:border-zinc-500 placeholder:text-zinc-600"
          />
          <button
            onClick={handleCreate}
            disabled={creating || !newName.trim()}
            className="px-4 py-2 text-xs font-semibold bg-yellow-500 hover:bg-yellow-400 text-black rounded-lg transition-colors disabled:opacity-50"
          >
            {creating ? 'Membuat…' : 'Buat & Copy Link'}
          </button>
          <button
            onClick={() => { setShowForm(false); setNewName('') }}
            className="p-2 text-zinc-500 hover:text-zinc-300"
          >
            <X size={14} />
          </button>
        </div>
      )}

      {loading ? (
        <p className="px-5 py-4 text-sm text-zinc-500">Loading…</p>
      ) : sessions.length === 0 ? (
        <p className="px-5 py-4 text-sm text-zinc-600">Belum ada sesi. Buat sesi untuk memberi akses operator.</p>
      ) : (
        <div className="divide-y divide-zinc-800">
          {sessions.map(s => (
            <div key={s.id} className="flex items-center justify-between px-5 py-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${s.is_active ? 'bg-green-400' : 'bg-zinc-600'}`} />
                <span className={`text-sm ${s.is_active ? 'text-white' : 'text-zinc-500'}`}>{s.operator_name}</span>
                {!s.is_active && (
                  <span className="text-[10px] text-zinc-600 border border-zinc-800 rounded px-1.5 py-0.5 uppercase tracking-wider">revoked</span>
                )}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                <button
                  onClick={() => handleCopy(s)}
                  title="Copy link operator"
                  className={`flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-medium rounded transition-colors ${
                    copied === s.id
                      ? 'bg-green-800/40 text-green-400'
                      : 'bg-zinc-800 hover:bg-zinc-700 text-zinc-300'
                  }`}
                >
                  {copied === s.id ? (
                    <><Copy size={11} /> Copied!</>
                  ) : (
                    <><Link2 size={11} /> Copy Link</>
                  )}
                </button>
                {s.is_active && (
                  <button
                    onClick={() => handleRevoke(s)}
                    className="px-2.5 py-1 text-[11px] font-medium rounded bg-zinc-800 hover:bg-red-900/40 text-zinc-400 hover:text-red-400 transition-colors"
                  >
                    Revoke
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      {dialog}
    </div>
  )
}

export default function SaleEventCheckinPage() {
  const { event } = useOutletContext<SaleEventDetailContext>()
  const { id } = useParams<{ id: string }>()
  const { token } = useAuth()

  const [orders, setOrders] = useState<AdminSaleOrderWithPhase[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [checkingIn, setCheckingIn] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  function toggleExpand(orderId: string) {
    setExpanded(prev => {
      const next = new Set(prev)
      if (next.has(orderId)) next.delete(orderId)
      else next.add(orderId)
      return next
    })
  }

  useEffect(() => {
    if (!id || !token) return
    setLoading(true)
    getAdminSaleOrders(token, id)
      .then(o => setOrders(o.filter(ord => ord.status === 'confirmed')))
      .finally(() => setLoading(false))
  }, [id, token])

  // Auto-expand orders that match search by ticket_code
  useEffect(() => {
    const q = search.trim().toLowerCase()
    if (!q) return
    const matchedIds = orders
      .filter(o => o.tickets.some(t => t.ticket_code.toLowerCase().includes(q)))
      .map(o => o.id)
    if (matchedIds.length > 0) {
      setExpanded(prev => {
        const next = new Set(prev)
        matchedIds.forEach(id => next.add(id))
        return next
      })
    }
  }, [search, orders])

  async function handleCheckin(ticketCode: string) {
    if (!token || !id) return
    setCheckingIn(ticketCode)
    setError(null)
    try {
      await checkInTicket(token, id, ticketCode)
      setOrders(prev => prev.map(o => ({
        ...o,
        tickets: o.tickets.map(t =>
          t.ticket_code === ticketCode
            ? { ...t, is_checked_in: true, checked_in_at: new Date().toISOString(), checked_in_by: 'Admin' }
            : t
        ),
      })))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Gagal check-in')
    } finally {
      setCheckingIn(null)
    }
  }

  const q = search.trim().toLowerCase()
  const filtered = orders.filter(o =>
    !q ||
    o.buyer_name.toLowerCase().includes(q) ||
    o.order_code.toLowerCase().includes(q) ||
    o.buyer_phone.includes(q) ||
    o.tickets.some(t => t.ticket_code.toLowerCase().includes(q))
  )

  const sorted = [
    ...filtered.filter(o => o.tickets.some(t => !t.is_checked_in) || o.tickets.length === 0),
    ...filtered.filter(o => o.tickets.length > 0 && o.tickets.every(t => t.is_checked_in)),
  ]

  const totalTickets = orders.reduce((acc, o) => acc + o.tickets.length, 0)
  const checkinCount = orders.reduce((acc, o) => acc + o.tickets.filter(t => t.is_checked_in).length, 0)

  if (loading) return <div className="p-6"><p className="text-zinc-500 text-sm">Loading…</p></div>

  return (
    <div className="p-6 space-y-4">

      {/* Operator sessions panel */}
      {id && token && (
        <SessionsPanel eventId={id} authToken={token} />
      )}

      {/* Progress summary */}
      <div className="rounded-xl border border-zinc-800 bg-zinc-900 p-5 flex items-center justify-between">
        <div>
          <p className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider mb-1">Progress Absensi</p>
          <p className="text-sm text-zinc-400">{event.title}</p>
        </div>
        <div className="text-right">
          <p className="text-3xl font-bold text-white">{checkinCount}</p>
          <p className="text-xs text-zinc-500 mt-0.5">dari {totalTickets} tiket dikonfirmasi</p>
          {totalTickets > 0 && (
            <div className="mt-2 h-1.5 w-32 bg-zinc-800 rounded-full overflow-hidden ml-auto">
              <div
                className="h-full bg-green-500 rounded-full transition-all"
                style={{ width: `${(checkinCount / totalTickets) * 100}%` }}
              />
            </div>
          )}
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Cari nama, kode order, atau nomor HP..."
          className="w-full bg-zinc-800 text-white rounded-lg pl-9 pr-4 py-2.5 text-sm border border-zinc-700 focus:outline-none focus:border-zinc-500 placeholder:text-zinc-600"
        />
      </div>

      {error && (
        <p className="rounded border border-red-800 bg-red-900/20 px-3 py-2 text-xs text-red-400">{error}</p>
      )}

      {/* Attendee list */}
      {sorted.length === 0 ? (
        <div className="rounded-xl border border-zinc-800 bg-zinc-900 p-8 text-center">
          <p className="text-zinc-500 text-sm">
            {totalTickets === 0
              ? 'Belum ada order yang dikonfirmasi.'
              : 'Tidak ada hasil yang sesuai pencarian.'}
          </p>
        </div>
      ) : (
        <div className="rounded-xl border border-zinc-800 bg-zinc-900 overflow-hidden">
          <div className="divide-y divide-zinc-800">
            {sorted.map(o => {
              const checkedCount = o.tickets.filter(t => t.is_checked_in).length
              const allChecked = o.tickets.length > 0 && checkedCount === o.tickets.length
              const singleTicket = o.tickets.length === 1
              const firstTicket = o.tickets[0]

              return (
                <div
                  key={o.id}
                  className={`transition-colors ${allChecked ? 'bg-green-900/10' : 'hover:bg-zinc-800/40'}`}
                >
                  {/* Order header */}
                  <div className="flex items-center justify-between px-5 py-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-white">{o.buyer_name}</span>
                        {o.sale_event_phases && (
                          <span className="text-[10px] text-zinc-500 border border-zinc-700 rounded px-1.5 py-0.5">
                            {o.sale_event_phases.name}
                          </span>
                        )}
                        <span className="text-[10px] text-zinc-600">{o.quantity} tiket</span>
                      </div>
                      <p className="text-xs text-zinc-500 mt-0.5 font-mono">{o.order_code}</p>
                    </div>
                    <div className="shrink-0 ml-4 text-right">
                      {allChecked ? (
                        <span className="text-xs font-semibold text-green-400">✓ Hadir</span>
                      ) : (
                        <>
                          {singleTicket ? (
                            <span className="text-xs text-zinc-400">{checkedCount}/{o.tickets.length} tiket</span>
                          ) : (
                            <div className="flex items-center gap-1.5">
                              <span className="text-xs text-zinc-400">{checkedCount}/{o.tickets.length} tiket</span>
                              <button
                                onClick={() => toggleExpand(o.id)}
                                className="text-zinc-500 hover:text-zinc-300 transition-colors"
                              >
                                {expanded.has(o.id) ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                              </button>
                            </div>
                          )}
                          {singleTicket && firstTicket && (
                            <div className="mt-1">
                              <button
                                onClick={() => handleCheckin(firstTicket.ticket_code)}
                                disabled={checkingIn === firstTicket.ticket_code}
                                className="px-4 py-1.5 text-xs font-semibold bg-zinc-700 hover:bg-zinc-600 text-white rounded-lg transition-colors disabled:opacity-50"
                              >
                                {checkingIn === firstTicket.ticket_code ? 'Memproses…' : 'Check In'}
                              </button>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  {/* Per-ticket sub-rows for multi-ticket orders */}
                  {!singleTicket && expanded.has(o.id) && o.tickets.map(t => (
                    <div key={t.id} className="flex items-center justify-between px-5 py-2 pl-10 bg-zinc-800/20 border-t border-zinc-800/50">
                      <span className="text-xs text-zinc-400 font-mono">
                        Tiket {t.ticket_index} · {t.ticket_code}
                      </span>
                      {t.is_checked_in ? (
                        <div className="text-right">
                          <span className="text-xs font-semibold text-green-400">✓</span>
                          {t.checked_in_at && (
                            <p className="text-[10px] text-zinc-600">
                              {new Date(t.checked_in_at).toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' })}
                            </p>
                          )}
                        </div>
                      ) : (
                        <button
                          onClick={() => handleCheckin(t.ticket_code)}
                          disabled={checkingIn === t.ticket_code}
                          className="px-3 py-1 text-[11px] font-semibold bg-zinc-700 hover:bg-zinc-600 text-white rounded-lg transition-colors disabled:opacity-50"
                        >
                          {checkingIn === t.ticket_code ? '…' : 'Check In'}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
